import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import TopBar from "../LandingPage/TopBar";
import { jwtDecode } from "jwt-decode";
import { trackEvent } from "../analytics";

const USE_MOCK_PAGE = process.env.REACT_APP_USE_MOCK_PAGE === "true";

const ConnectorPage = ({ token, onAccessTokenUpdated }) => {
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);

  // Parse URL parameters on page load
  useEffect(() => {
    if (USE_MOCK_PAGE) {
      return;
    }
    const urlParams = new URLSearchParams(window.location.search);
    const status = urlParams.get("status");
    const error = urlParams.get("error");

    if (status === "success") {
      // Simulate adding the accessToken to the token
      const updatedToken = {
        ...jwtDecode(token),
        accessToken: "newAccessToken",
      };
      const encodedToken = btoa(JSON.stringify(updatedToken)); // Re-encode the updated token
      localStorage.setItem("token", encodedToken);

      onAccessTokenUpdated(); // Notify parent to update state
      trackEvent("Facebook Connected");
      alert("Facebook connected successfully!");
      navigate("/dashboard"); // Redirect to the dashboard
    }

    if (error) {
      // Handle error scenario
      trackEvent("Facebook Connect Failed", { error });
      alert(`Error: ${error}`);
    }
  }, [token, navigate, onAccessTokenUpdated]);

  const handleFacebookLogin = async () => {
    try {
      if (USE_MOCK_PAGE) {
        onAccessTokenUpdated();
        trackEvent("Facebook Connected (Mock)");
        navigate("/dashboard");
        return;
      }

      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      if (!backendUrl) {
        throw new Error("Backend URL is not defined");
      }
      trackEvent("Connect Facebook");
      // Redirect to backend for OAuth flow with token
      window.location.href = `${backendUrl}/auth/facebook?token=${encodeURIComponent(
        token
      )}`;
    } catch (error) {
      console.error("Error during authentication:", error);
    }
  };

  return (
    <>
      <TopBar />

      <div className="flex flex-col items-center justify-center min-h-[calc(100vh-64px)] px-5 pt-8 pb-8 sm:pt-12 sm:pb-12 bg-gradient-to-br from-gray-50 via-white to-primary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <div className="max-w-md w-full">
          <div className="card-modern p-8 md:p-10">
            <div className="text-center mb-8">
              <div className="inline-block mb-4 px-4 py-2 bg-primary-100 dark:bg-primary-900/30 rounded-full">
                <span className="text-primary-700 dark:text-primary-300 text-sm font-semibold">
                  🔗 Connect Your Account
                </span>
              </div>
              <Typography
                variant="h4"
                component="h1"
                className="text-3xl sm:text-4xl font-extrabold mb-4 text-gray-900 dark:text-white"
              >
                Attach Instagram Account
              </Typography>
              <div className="max-w-lg mx-auto">
                <Typography
                  variant="body1"
                  className="text-base sm:text-lg text-gray-600 dark:text-gray-400 text-center"
                >
                  Login with Facebook to access your Instagram account
                </Typography>
              </div>
            </div>

            <div className="space-y-4">
              <Button
                variant="contained"
                onClick={handleFacebookLogin}
                className="w-full bg-primary-600 hover:bg-primary-700 text-white font-semibold px-8 py-3.5 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 normal-case text-base sm:text-lg"
              >
                Connect with Facebook
              </Button>

              <div className="text-center">
                <button
                  onClick={() => setDialogOpen(true)}
                  className="text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200 underline"
                >
                  Why do I need to login with Facebook?
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Dialog for Facebook explanation */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          className: "bg-white dark:bg-gray-800 rounded-2xl shadow-soft-lg border border-gray-100 dark:border-gray-700"
        }}
      >
        <DialogTitle className="text-2xl font-bold text-gray-900 dark:text-white pb-3 pt-6 px-6">
          <div className="inline-block mb-2 px-3 py-1 bg-primary-100 dark:bg-primary-900/30 rounded-full">
            <span className="text-primary-700 dark:text-primary-300 text-sm font-semibold">
              🔐 Security & Integration
            </span>
          </div>
          <br />
          Why Facebook Login?
        </DialogTitle>
        <DialogContent className="px-6 py-6">
          <Typography className="text-base text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
            Meta requires that an Instagram account must be connected to a
            Facebook page in order to use a 3rd party platform like ReelTale.
            This is a security requirement from Meta to ensure proper
            authentication and access control.
          </Typography>
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
            <Typography className="text-sm text-blue-800 dark:text-blue-200">
              💡 <strong>Tip:</strong> You don't need to actively use the Facebook Page. You can create one for free in minutes if you don't have one.
            </Typography>
          </div>
        </DialogContent>
        <DialogActions className="px-6 pb-6 pt-2">
          <Button
            onClick={() => setDialogOpen(false)}
            className="bg-primary-600 hover:bg-primary-700 text-white font-semibold px-6 py-2 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 normal-case"
          >
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ConnectorPage;
